import { useState, useEffect, useCallback } from "react";
import { api } from "../../../lib/api";
import { fmtDT } from "../../../lib/constants";
import { toast } from "sonner";
import { Prohibit, CheckCircle, ShieldWarning } from "@phosphor-icons/react";
import { Panel } from "./_shared";

// Module 9B U2 — Shipment Block foundation UI. Internal viewers only (SA/CTOP/CTRL/CAP/RID).
// Side is inferred from lifecycle status (never chosen). Backend authorization is authoritative;
// these controls only mirror it. DESTINATION-owned from ACCEPTED_BY_AIRLINE onward.
const DEST_STATUSES = new Set([
  "ACCEPTED_BY_AIRLINE", "ARRIVED_DESTINATION_AIRPORT", "AWAITING_RECEIVER_READY",
  "RECEIVER_READY_CONFIRMED", "DESTINATION_VEHICLE_ASSIGNED", "OUT_FOR_DELIVERY",
]);
const TERMINAL_STATUSES = new Set(["DELIVERED", "CANCELLED"]);

const placerLine = (name, role, at) =>
  `${name || "—"}${role ? ` · ${role}` : ""} · ${fmtDT(at)}`;

export function ShipmentBlockPanel({ d, profile }) {
  const s = d.shipment;
  const sid = s.id;
  const role = profile?.role;
  const [data, setData] = useState(null);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/shipments/${sid}/blocks`)
      .then((r) => setData(r.data))
      .catch(() => setData({ active: null, history: [] }));
  }, [sid]);
  useEffect(() => { load(); }, [load]);

  const active = data?.active || null;
  const history = data?.history || [];

  const side = DEST_STATUSES.has(s.current_status) ? "DESTINATION" : "SOURCE";
  const isTerminal = TERMINAL_STATUSES.has(s.current_status);
  const sideCtopId = side === "SOURCE" ? s.source_ctop_id : s.destination_ctop_id;
  const sideCtrlId = side === "SOURCE" ? s.source_city_controller_id : s.destination_city_controller_id;

  const canPlace = !isTerminal && !active && (
    role === "SUPER_ADMIN" ||
    (role === "CTOP" && profile?.id === sideCtopId) ||
    (role === "CTRL" && profile?.id === sideCtrlId)
  );

  const activeSideCtrlId = active
    ? (active.side === "SOURCE" ? s.source_city_controller_id : s.destination_city_controller_id)
    : null;
  const canResolve = !!active && (
    role === "SUPER_ADMIN" ||
    (role === "CTRL" && profile?.id === activeSideCtrlId)
  );

  const place = async () => {
    if (reason.trim().length < 3) return toast.error("Placement reason required (min 3 chars)");
    setBusy(true);
    try {
      await api.post(`/shipments/${sid}/block`, { reason: reason.trim() });
      toast.success("Shipment Block placed");
      setReason("");
      load();
    } catch (e) { toast.error(e.displayMessage || "Could not place block"); }
    finally { setBusy(false); }
  };

  const resolve = async () => {
    if (note.trim().length < 3) return toast.error("Resolution note required (min 3 chars)");
    setBusy(true);
    try {
      await api.post(`/shipments/${sid}/block/resolve`, { resolution_note: note.trim() });
      toast.success("Shipment Block resolved");
      setNote("");
      load();
    } catch (e) { toast.error(e.displayMessage || "Could not resolve block"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="shipment-block-panel">
      <Panel icon={<Prohibit size={16} weight="bold" />} title="SHIPMENT BLOCK">
        {active ? (
          <div className="mb-3 rounded p-3 bg-[hsl(var(--danger)/0.1)] border border-[hsl(var(--danger)/0.35)]" data-testid="shipment-block-active-banner">
            <div className="flex items-center gap-2 mb-1">
              <ShieldWarning size={16} weight="bold" className="text-[hsl(var(--danger))]" />
              <span className="text-sm font-semibold text-[hsl(var(--danger))]">Shipment Block active</span>
              <span className="status-pill status-warning ml-auto" data-testid="shipment-block-active-side">{active.side}</span>
            </div>
            <div className="text-sm text-[hsl(var(--fg))]" data-testid="shipment-block-active-reason">{active.reason}</div>
            <div className="text-[11px] text-[hsl(var(--muted))] mt-1" data-testid="shipment-block-active-meta">
              Placed by {placerLine(active.placed_by_name, active.placed_by_role, active.placed_at)}
            </div>
          </div>
        ) : (
          <div className="text-xs text-[hsl(var(--muted))] mb-3" data-testid="shipment-block-none">No active block on this shipment.</div>
        )}

        {canPlace && (
          <div className="mb-3 space-y-2" data-testid="shipment-block-place-form">
            <textarea
              className="input-field w-full text-sm" rows={2} placeholder={`Reason to place a ${side} block…`}
              value={reason} onChange={(e) => setReason(e.target.value)}
              data-testid="shipment-block-reason-input" />
            <button className="btn-primary text-sm" disabled={busy} onClick={place} data-testid="shipment-block-place-btn">
              <Prohibit size={13} /> Place Block
            </button>
          </div>
        )}

        {active && canResolve && (
          <div className="mb-3 space-y-2" data-testid="shipment-block-resolve-form">
            <textarea
              className="input-field w-full text-sm" rows={2} placeholder="Resolution note…"
              value={note} onChange={(e) => setNote(e.target.value)}
              data-testid="shipment-block-resolve-input" />
            <button className="btn-ghost text-sm" disabled={busy} onClick={resolve} data-testid="shipment-block-resolve-btn">
              <CheckCircle size={13} /> Resolve Block
            </button>
          </div>
        )}

        <div className="overline mb-2">HISTORY</div>
        {history.length === 0 ? (
          <div className="text-xs text-[hsl(var(--muted))]" data-testid="shipment-block-history-empty">No block history.</div>
        ) : (
          <div className="space-y-2" data-testid="shipment-block-history">
            {history.map((b) => (
              <div key={b.id} className="border border-[hsl(var(--border))] rounded p-2.5" data-testid={`shipment-block-row-${b.id}`}>
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="status-pill status-info">{b.side}</span>
                  <span className={`status-pill ${b.status === "ACTIVE" ? "status-warning" : "status-success"}`} data-testid="shipment-block-row-status">{b.status}</span>
                </div>
                <div className="text-xs text-[hsl(var(--fg))]">{b.reason}</div>
                <div className="text-[11px] text-[hsl(var(--muted))] mt-1">
                  Placed by {placerLine(b.placed_by_name, b.placed_by_role, b.placed_at)}
                  {b.status === "RESOLVED" && (
                    <> · Resolved by {placerLine(b.resolved_by_name, b.resolved_by_role, b.resolved_at)}{b.resolution_note ? ` — ${b.resolution_note}` : ""}</>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
